import { DEVELOPER_GROUP_LABELS, type DeveloperGroup } from '@company/shared'
import { X, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useActiveEmployees } from '@/hooks/hr'
import {
  useBulkAssign,
  useProjectMembers,
  type BulkAssignInput,
  type BulkAssignResult,
  type Issue,
} from '@/hooks/pm'
import { flattenMembers } from '@/lib/member-utils'

interface BatchAssignmentDialogProps {
  issues: Issue[]
  projectId: string
  onClose: () => void
  onSuccess?: () => void
}

export function BatchAssignmentDialog({
  issues,
  projectId,
  onClose,
  onSuccess,
}: BatchAssignmentDialogProps) {
  const [changeOwner, setChangeOwner] = useState(false)
  const [addParticipants, setAddParticipants] = useState(false)
  const [removeParticipants, setRemoveParticipants] = useState(false)
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>('')
  const [selectedAddIds, setSelectedAddIds] = useState<string[]>([])
  const [selectedRemoveIds, setSelectedRemoveIds] = useState<string[]>([])
  const [selectedGroup, setSelectedGroup] = useState<DeveloperGroup | ''>('')
  const [result, setResult] = useState<BulkAssignResult | null>(null)

  const { data: groupedMembers } = useProjectMembers(projectId)
  const { data: activeEmployees } = useActiveEmployees()
  const bulkAssign = useBulkAssign()

  const members = flattenMembers(groupedMembers)

  // 创建员工ID到名称的映射
  const employeeNameMap = new Map<string, string>()
  activeEmployees?.forEach((emp) => {
    employeeNameMap.set(emp.id, emp.name)
  })

  const filteredMembers = members.filter((m) => {
    if (selectedGroup && m.developerGroup !== selectedGroup) { return false }
    return true
  })

  const handleSubmit = () => {
    const input: BulkAssignInput = {
      issueIds: issues.map((i) => i.id),
    }

    if (changeOwner) {
      input.ownerId = selectedOwnerId || null
    }
    if (addParticipants && selectedAddIds.length > 0) {
      input.addParticipantIds = selectedAddIds
    }
    if (removeParticipants && selectedRemoveIds.length > 0) {
      input.removeParticipantIds = selectedRemoveIds
    }

    bulkAssign.mutate(input, {
      onSuccess: (data: BulkAssignResult) => {
        setResult(data)
        if (data.success && data.failureCount === 0) {
          onSuccess?.()
        }
      },
    })
  }

  const toggleAddParticipant = (id: string) => {
    setSelectedAddIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const toggleRemoveParticipant = (id: string) => {
    setSelectedRemoveIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  // 结果显示
  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
          {result.failureCount === 0 ? (
            <>
              <div className="mb-4 flex items-center gap-2 text-green-600">
                <CheckCircle className="h-6 w-6" />
                <h3 className="text-lg font-medium">批量分配成功</h3>
              </div>
              <p className="mb-4 text-sm text-muted-foreground">
                已更新 {result.successCount} 个任务的分配
              </p>
            </>
          ) : (
            <>
              <div className="mb-4 flex items-center gap-2 text-red-600">
                <XCircle className="h-6 w-6" />
                <h3 className="text-lg font-medium">批量分配失败</h3>
              </div>
              <p className="mb-2 text-sm text-muted-foreground">
                以下任务分配失败:
              </p>
              <div className="mb-4 max-h-40 space-y-1 overflow-y-auto rounded-md border p-2">
                {result.failures.map((f: any) => (
                  <div key={f.issueId} className="text-sm">
                    <span className="font-mono">{f.issueCode}</span>: {f.error}
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                其他任务未做任何修改
              </p>
            </>
          )}
          <div className="mt-4 flex justify-end">
            <button
              onClick={onClose}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              确定
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-background shadow-lg">
        {/* 标题 */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-medium">批量分配任务</h3>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-4">
          {/* 已选任务 */}
          <div className="mb-4">
            <div className="mb-2 text-sm font-medium">已选择 {issues.length} 个任务:</div>
            <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border p-2">
              {issues.map((issue) => (
                <div key={issue.id} className="text-sm">
                  <span className="font-mono">{issue.code}</span> {issue.title}
                </div>
              ))}
            </div>
          </div>

          {/* 开发组筛选 */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium">开发组筛选</label>
            <Select
              value={selectedGroup || 'all'}
              onValueChange={(value) => setSelectedGroup(value === 'all' ? '' : value as DeveloperGroup)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="全部开发组" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部开发组</SelectItem>
                {(Object.keys(DEVELOPER_GROUP_LABELS) as DeveloperGroup[]).map((group) => (
                  <SelectItem key={group} value={group}>
                    {DEVELOPER_GROUP_LABELS[group]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 分配操作 */}
          <div className="space-y-4">
            {/* 更换负责人 */}
            <div className="rounded-md border p-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={changeOwner}
                  onChange={(e) => setChangeOwner(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium">更换负责人</span>
              </label>
              {changeOwner && (
                <div className="mt-2">
                  <Select
                    value={selectedOwnerId || 'clear'}
                    onValueChange={(value) => setSelectedOwnerId(value === 'clear' ? '' : value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="清除负责人" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clear">清除负责人</SelectItem>
                      {filteredMembers.map((m) => (
                        <SelectItem key={m.employeeId} value={m.employeeId}>
                          {employeeNameMap.get(m.employeeId) || m.employeeId.slice(0, 8)}
                          {m.developerGroup && ` (${DEVELOPER_GROUP_LABELS[m.developerGroup]})`}
                          {m.isLeader && ' ⭐'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* 添加参与者 */}
            <div className="rounded-md border p-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={addParticipants}
                  onChange={(e) => setAddParticipants(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium">添加参与者</span>
              </label>
              {addParticipants && (
                <div className="mt-2 max-h-32 space-y-1 overflow-y-auto">
                  {filteredMembers.map((m) => (
                    <label key={m.employeeId} className="flex items-center gap-2 rounded p-1 hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={selectedAddIds.includes(m.employeeId)}
                        onChange={() => toggleAddParticipant(m.employeeId)}
                        className="rounded"
                      />
                      <span className="text-sm">
                        {employeeNameMap.get(m.employeeId) || m.employeeId.slice(0, 8)}
                        {m.developerGroup && ` (${DEVELOPER_GROUP_LABELS[m.developerGroup]})`}
                        {m.isLeader && ' ⭐'}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* 移除参与者 */}
            <div className="rounded-md border p-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={removeParticipants}
                  onChange={(e) => setRemoveParticipants(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium">移除参与者</span>
              </label>
              {removeParticipants && (
                <div className="mt-2 max-h-32 space-y-1 overflow-y-auto">
                  {filteredMembers.map((m) => (
                    <label key={m.employeeId} className="flex items-center gap-2 rounded p-1 hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={selectedRemoveIds.includes(m.employeeId)}
                        onChange={() => toggleRemoveParticipant(m.employeeId)}
                        className="rounded"
                      />
                      <span className="text-sm">
                        {employeeNameMap.get(m.employeeId) || m.employeeId.slice(0, 8)}
                        {m.developerGroup && ` (${DEVELOPER_GROUP_LABELS[m.developerGroup]})`}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 警告 */}
          <div className="mt-4 flex items-start gap-2 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>批量操作将应用到所有选中的任务</span>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <button
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={bulkAssign.isPending || (!changeOwner && !addParticipants && !removeParticipants)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {bulkAssign.isPending ? '处理中...' : '确认分配'}
          </button>
        </div>
      </div>
    </div>
  )
}
